import {
  SET_WORDS_LIST,
  SET_WORDS_LOADING,
  SET_WORDS_FILTERS,
  PATCH_WORD_MASTERY,
  SET_AUTO_MASTERY_UPDATES,
  SET_WORDS_PAGE,
  SET_WORDS_SELECTION,
  CLEAR_WORDS_CACHE,
  PATCH_WORDS_MASTERY_BULK,
  SET_WORDS_STATS,
  SET_WORDS_SELECT_ALL_MATCHING,
} from '../types'

export type Mastery5 = 'new' | 'learning' | 'growing' | 'developing' | 'mastered'

export interface WordTestLevel {
  test: 'HSK' | 'New HSK' | 'TOFCL' | string
  level: string
}

export interface WordItem {
  wordId: string
  word: string
  pinyin: string
  english: string
  frequency: number
  hasAudio: boolean
  encountered: boolean
  mastery: 'unknown' | 'learning' | 'mastered'
  mastery5?: Mastery5
  testLevels?: WordTestLevel[]
  lastSeenAt?: string | null
  // Server-computed: the learner keeps looking this word up mid-story and
  // hasn't marked it Known yet. `lookups` is the raw count behind it.
  struggling?: boolean
  lookups?: number
}

export interface WordsLevelFilters {
  hsk: string[]
  newHsk: string[]
  tofcl: string[]
}

export interface WordsStats {
  new: number
  learning: number
  growing: number
  developing: number
  mastered: number
}

interface PageCacheEntry {
  items: WordItem[]
  total: number
}

interface IWordsState {
  items: WordItem[]
  page: number
  perPage: number
  total: number
  loading: boolean
  error: string | null
  filters: { search: string; mastery: string[]; levels: WordsLevelFilters; struggling: boolean }
  auto_mastery_updates: boolean
  cache: Record<string, PageCacheEntry>
  selectedIds: string[]
  // When true, the bulk action targets EVERY word matching the active filters
  // (across all pages) rather than the explicit `selectedIds` list.
  selectAllMatching: boolean
  stats: WordsStats
  statsTotal: number
  statsLoading: boolean
}

const emptyLevels: WordsLevelFilters = { hsk: [], newHsk: [], tofcl: [] }
const emptyStats: WordsStats = { new: 0, learning: 0, growing: 0, developing: 0, mastered: 0 }

const initialState: IWordsState = {
  items: [],
  page: 1,
  perPage: 50,
  total: 0,
  loading: false,
  error: null,
  filters: { search: '', mastery: [], levels: emptyLevels, struggling: false },
  auto_mastery_updates: true,
  cache: {},
  selectedIds: [],
  selectAllMatching: false,
  stats: emptyStats,
  statsTotal: 0,
  statsLoading: false,
}

const lv = (levels?: Partial<WordsLevelFilters>): WordsLevelFilters => ({
  hsk: levels?.hsk || [],
  newHsk: levels?.newHsk || [],
  tofcl: levels?.tofcl || [],
})

export const filtersKey = (filters: {
  search: string
  mastery: string[]
  levels?: WordsLevelFilters
  struggling?: boolean
}): string => {
  const m = [...filters.mastery].sort().join(',')
  const l = lv(filters.levels)
  const hsk = [...l.hsk].sort().join(',')
  const nhsk = [...l.newHsk].sort().join(',')
  const tofcl = [...l.tofcl].sort().join(',')
  return `s=${filters.search || ''}|m=${m}|h=${hsk}|nh=${nhsk}|t=${tofcl}|st=${filters.struggling ? 1 : 0}`
}

const cacheKey = (
  filters: { search: string; mastery: string[]; levels?: WordsLevelFilters; struggling?: boolean },
  page: number,
): string => `${filtersKey(filters)}|p=${page}`

const applyMasteryToCache = (
  cache: Record<string, PageCacheEntry>,
  wordIds: string[],
  mastery: WordItem['mastery'],
): Record<string, PageCacheEntry> => {
  const idSet = new Set(wordIds)
  const next: Record<string, PageCacheEntry> = {}
  for (const key of Object.keys(cache)) {
    const entry = cache[key]
    next[key] = {
      total: entry.total,
      items: entry.items.map((w) => (idSet.has(w.wordId) ? { ...w, mastery } : w)),
    }
  }
  return next
}

export default function wordsReducer(state = initialState, action: any) {
  switch (action.type) {
    case SET_WORDS_LOADING:
      return { ...state, loading: action.payload }
    case SET_WORDS_LIST: {
      const { items, page, perPage, total, filters } = action.payload
      const usedFilters = filters || state.filters
      const key = cacheKey(usedFilters, page)
      const cache = { ...state.cache, [key]: { items, total } }
      const isCurrent =
        page === state.page &&
        filtersKey(usedFilters) === filtersKey(state.filters)
      return {
        ...state,
        cache,
        items: isCurrent ? items : state.items,
        total: isCurrent ? total : state.total,
        perPage,
        // Only clear the spinner when THIS response is for the view on screen.
        // A stale/prefetch response (different filter or page) must not flip
        // loading off while the current request is still in flight, or the
        // "No words match your filters." empty state flashes between the
        // spinner and the real results.
        loading: isCurrent ? false : state.loading,
        error: null,
      }
    }
    case SET_WORDS_PAGE: {
      const { page, filters } = action.payload
      const usedFilters = filters || state.filters
      const key = cacheKey(usedFilters, page)
      const cached = state.cache[key]
      return {
        ...state,
        page,
        filters: usedFilters,
        items: cached ? cached.items : [],
        total: cached ? cached.total : state.total,
        loading: !cached,
        selectedIds: [],
        selectAllMatching: false,
      }
    }
    case SET_WORDS_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
          levels: action.payload.levels
            ? { ...state.filters.levels, ...action.payload.levels }
            : state.filters.levels,
        },
        selectedIds: [],
        selectAllMatching: false,
      }
    case SET_WORDS_STATS: {
      const { stats, total, loading } = action.payload
      return {
        ...state,
        stats: stats || state.stats,
        statsTotal: typeof total === 'number' ? total : state.statsTotal,
        statsLoading: typeof loading === 'boolean' ? loading : false,
      }
    }
    case CLEAR_WORDS_CACHE:
      return { ...state, cache: {} }
    case SET_WORDS_SELECTION:
      // Any explicit selection edit drops out of "select all matching" mode.
      return { ...state, selectedIds: action.payload, selectAllMatching: false }
    case SET_WORDS_SELECT_ALL_MATCHING:
      return { ...state, selectAllMatching: action.payload }
    case PATCH_WORD_MASTERY: {
      const { wordId, mastery } = action.payload
      return {
        ...state,
        items: state.items.map((w) => (w.wordId === wordId ? { ...w, mastery } : w)),
        cache: applyMasteryToCache(state.cache, [wordId], mastery),
      }
    }
    case PATCH_WORDS_MASTERY_BULK: {
      const { wordIds, mastery } = action.payload
      const idSet = new Set<string>(wordIds)
      return {
        ...state,
        items: state.items.map((w) => (idSet.has(w.wordId) ? { ...w, mastery } : w)),
        cache: applyMasteryToCache(state.cache, wordIds, mastery),
      }
    }
    case SET_AUTO_MASTERY_UPDATES:
      return { ...state, auto_mastery_updates: action.payload }
    default:
      return state
  }
}
